import React, { useMemo } from "react";
import { Container, Graphics } from "@pixi/react";
import { usePrimaryToolbar } from "../../../../Hooks/usePrimaryToolbar";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../pixiSettings";
import { PixiSettings } from "../../../../pixiSettings";
import ButtonMenuClose from "../Buttons/ButtonMenuClose";
import ButtonHome from "../Buttons/ButtonHome";
import Clock from "../Other/Clock";
import CompanyLogo from "../CompanyLogo/CompanyLogo";

// Full opacity background for menu toolbar (not transparent like regular toolbars)
const TOOLBAR_BACKGROUND_COLOR = 0x090f41;
const TOOLBAR_BACKGROUND_OPACITY = 1.0;

// Same height config as regular ToolbarTop
const TOOLBAR_TOP_HEIGHT_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 1.0,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 1.0,
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 1.0,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 1.0,
  },
  [PixiSettings.DEVICE.DESKTOP]: 1.0,
};

const MenuToolbarTop = () => {
  const toolbar = usePrimaryToolbar();
  const { dimensions, device, orientation, getResponsiveConfig } =
    useDimensions();

  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  const topToolbar = useMemo(() => {
    const heightMultiplier =
      device === PixiSettings.DEVICE.DESKTOP
        ? TOOLBAR_TOP_HEIGHT_CONFIG[device]
        : (TOOLBAR_TOP_HEIGHT_CONFIG[device]?.[orientation] ?? 0.9);
    return {
      width: toolbar.width,
      height: buttonConfig.height * heightMultiplier,
    };
  }, [toolbar.width, device, orientation, buttonConfig.height]);

  return (
    <Container
      name="menu_toolbar_top"
      x={(dimensions.width - topToolbar.width) / 2}
      y={0}
      sortableChildren
    >
      {/* Full-width background (extends beyond toolbar cap to fill screen) */}
      <Graphics
        name="menu_toolbar_top_background"
        draw={(g) => {
          g.clear();
          const offset = (dimensions.width - topToolbar.width) / 2;
          g.beginFill(TOOLBAR_BACKGROUND_COLOR, TOOLBAR_BACKGROUND_OPACITY);
          g.drawRect(-offset, 0, dimensions.width, topToolbar.height);
          g.endFill();
        }}
      />

      {/* Full-width shadow below toolbar */}
      <Graphics
        name="menu_toolbar_top_shadow"
        draw={(g) => {
          g.clear();
          const offset = (dimensions.width - topToolbar.width) / 2;
          const shadowHeight = 45;
          for (let i = 0; i < shadowHeight; i++) {
            const t = 1 - i / shadowHeight;
            const alpha = t * t * 0.9;
            g.beginFill(0x060b30, alpha);
            g.drawRect(-offset, topToolbar.height + i, dimensions.width, 1);
            g.endFill();
          }
        }}
      />

      <CompanyLogo toolbar={topToolbar} />
      <ButtonHome toolbar={topToolbar} />
      <Clock toolbar={topToolbar} />
      <ButtonMenuClose toolbar={topToolbar} />
    </Container>
  );
};

export default MenuToolbarTop;
